import React, { useCallback, useEffect, useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, ActivityIndicator, Image } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../context/ThemeContext';
import { Typography } from '../components/ui';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { api, getFileUrl } from '../services/api';
import {
  formatAccrual,
  formatMoney,
  hasRefunds,
  isEmptyReport,
  operationAmount,
  operationStatusLabel,
  payoutStatusLabel,
  termsExplanation,
  termsSummary,
} from './finance/masterSales';
import {
  FINANCE_PERIODS,
  type FinancePeriod,
  type MasterSalesReport,
  type SalesOperationList,
} from '../types/masterSales';

import { ON_ACCENT_TEXT } from '../styles/accentText';
/**
 * CORE-881 — «Финансы → Продажи» мастера.
 *
 * Зеркало пользовательских «Покупок» со стороны продавца и сознательно не CRM:
 * агрегаты по программам, список операций и условия расчёта. Списка клиентов с
 * контактами здесь нет — из данных о покупателе приходит только имя.
 *
 * Ни одна сумма на этом экране не вычисляется. Gross, комиссия, возвраты и
 * начисление приходят посчитанными с сервера: две реализации одной формулы
 * однажды разойдутся, и разойдутся они на деньгах.
 */

export default function MasterSalesScreen() {
  const { colors: C } = useTheme();

  const [period, setPeriod] = useState<FinancePeriod>('30d');
  const [report, setReport] = useState<MasterSalesReport | null>(null);
  const [ops, setOps] = useState<SalesOperationList | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (p: FinancePeriod) => {
    setLoading(true);
    setFailed(false);
    try {
      const [r, o] = await Promise.all([
        api.getMasterSales({ period: p, limit: 50 }),
        api.getMasterSalesOperations({ period: p, limit: 30 }),
      ]);
      setReport(r);
      setOps(o);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(period); }, [load, period]);

  const terms = report?.terms;
  const explanation = terms ? termsExplanation(terms) : '';
  const summary = terms ? termsSummary(terms) : '';

  /**
   * Расчётная сумма или объяснение вместо неё.
   *
   * Прочерк здесь не годится: он читается как ноль, а ноль означал бы, что
   * мастер получает всю выручку.
   */
  const accrual = (amount: number | null, currency: string) => {
    const text = formatAccrual(amount, currency);
    return text ?? 'не рассчитано';
  };

  return (
    <View style={[s.root, { backgroundColor: C.pageBg }]}>
      <Header />
      <ScrollView style={s.scroll} contentContainerStyle={s.scrollContent}>
        <View style={s.content}>
          <Typography variant="h2" style={{ color: C.textPrimary }}>Продажи</Typography>

          <View style={s.periods}>
            {FINANCE_PERIODS.map((p) => {
              const active = p.code === period;
              return (
                <TouchableOpacity
                  key={p.code}
                  onPress={() => setPeriod(p.code)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  testID={`salesPeriod-${p.code}`}
                  style={[
                    s.period,
                    { borderColor: active ? C.solar : C.border, backgroundColor: active ? C.solar : 'transparent' },
                  ]}
                >
                  <Typography variant="caption" style={{ color: active ? ON_ACCENT_TEXT : C.textPrimary }}>
                    {p.label}
                  </Typography>
                </TouchableOpacity>
              );
            })}
          </View>

          {explanation !== '' && (
            <View style={[s.notice, { backgroundColor: C.backgroundSoft, borderColor: C.borderSubtle }]}>
              <Ionicons name="information-circle-outline" size={16} color={C.textMuted} />
              <Typography variant="caption" style={{ color: C.textSecondary, flex: 1 }}>
                {explanation}
              </Typography>
            </View>
          )}

          {summary !== '' && (
            <Typography variant="caption" style={{ color: C.textMuted, marginBottom: 4 }}>
              {summary}
            </Typography>
          )}

          {loading ? (
            <ActivityIndicator color={C.textMuted} style={{ marginTop: 32 }} />
          ) : failed ? (
            <View style={s.empty}>
              <Typography variant="body" style={{ color: C.textSecondary }}>
                Не получилось загрузить продажи.
              </Typography>
              <TouchableOpacity
                onPress={() => void load(period)}
                accessibilityRole="button"
                style={[s.retry, { borderColor: C.border }]}
              >
                <Typography variant="body" style={{ color: C.textPrimary }}>Попробовать снова</Typography>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              {(report?.totals ?? []).map((t) => (
                <View key={t.currency} style={[s.totals, { backgroundColor: C.backgroundSoft, borderColor: C.borderSubtle }]}>
                  <View style={s.totalsRow}>
                    <Metric label="Продаж" value={String(t.paidCount)} C={C} />
                    <Metric label="Выручка" value={formatMoney(t.gross, t.currency)} C={C} />
                    {t.refundedCount > 0 && (
                      <Metric label="Возвраты" value={formatMoney(t.refunded, t.currency)} C={C} />
                    )}
                  </View>
                  <View style={[s.totalsRow, { borderTopColor: C.borderSubtle, borderTopWidth: 1, paddingTop: 12 }]}>
                    <Metric label="Комиссия" value={accrual(t.commission, t.currency)} C={C} />
                    <Metric label="Начислено" value={accrual(t.masterAccrual, t.currency)} C={C} />
                    <Metric label="К выплате" value={accrual(t.availableForPayout, t.currency)} C={C} />
                  </View>
                </View>
              ))}

              {isEmptyReport(report?.items ?? []) ? (
                <View style={s.empty}>
                  <Ionicons name="pricetag-outline" size={26} color={C.textMuted} />
                  <Typography variant="body" style={{ color: C.textMuted, marginTop: 10, textAlign: 'center' }}>
                    За этот период продаж не было.
                  </Typography>
                </View>
              ) : (
                <View style={s.list} testID="salesProgramList">
                  {(report?.items ?? []).map((row) => (
                    <View
                      key={`${row.programId}-${row.currency}`}
                      style={[s.card, { backgroundColor: C.backgroundSoft, borderColor: C.borderSubtle }]}
                    >
                      <View style={s.cardHead}>
                        {row.coverImageUrl ? (
                          <Image source={{ uri: getFileUrl(row.coverImageUrl) ?? undefined }} style={s.cover} />
                        ) : (
                          <View style={[s.cover, s.coverEmpty, { backgroundColor: C.backgroundAlt }]}>
                            <Ionicons name="albums-outline" size={18} color={C.textMuted} />
                          </View>
                        )}
                        <View style={{ flex: 1 }}>
                          <Typography variant="body" style={[s.cardTitle, { color: C.textPrimary }]}>
                            {row.title || 'Без названия'}
                          </Typography>
                          <Typography variant="caption" style={{ color: C.textMuted }}>
                            {payoutStatusLabel(row.payoutStatus)}
                          </Typography>
                        </View>
                      </View>

                      <View style={[s.cardRow, { borderTopColor: C.borderSubtle }]}>
                        <Metric label="Продаж" value={String(row.paidCount)} C={C} small />
                        <Metric label="Выручка" value={formatMoney(row.gross, row.currency)} C={C} small />
                        {hasRefunds(row) && (
                          <Metric label="Возвраты" value={formatMoney(row.refunded, row.currency)} C={C} small />
                        )}
                        <Metric label="Начислено" value={accrual(row.masterAccrual, row.currency)} C={C} small />
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {(ops?.items?.length ?? 0) > 0 && (
                <View style={s.opsBlock}>
                  <Typography variant="h3" style={[s.opsTitle, { color: C.textPrimary }]}>Операции</Typography>
                  {(ops?.items ?? []).map((op) => (
                    <View
                      key={op.purchaseId}
                      style={[s.opRow, { borderBottomColor: C.borderSubtle }]}
                    >
                      <View style={{ flex: 1 }}>
                        <Typography variant="body" style={{ color: C.textPrimary }}>
                          {op.title || 'Без названия'}
                        </Typography>
                        <Typography variant="caption" style={{ color: C.textMuted }}>
                          {operationStatusLabel(op)}
                          {op.buyerName ? ` · ${op.buyerName}` : ''}
                        </Typography>
                      </View>
                      <View style={{ alignItems: 'flex-end' }}>
                        <Typography
                          variant="body"
                          style={{ color: op.status === 'refunded' ? C.error : C.textPrimary, fontWeight: '600' }}
                        >
                          {operationAmount(op)}
                        </Typography>
                        <Typography variant="caption" style={{ color: C.textMuted }}>
                          {accrual(op.masterAccrual, op.currency)}
                        </Typography>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </>
          )}
        </View>
        {/* CORE-1743: подвал внутри прокрутки. Снаружи он забирал высоту окна, и на
            ноутбуке страница сжималась до нуля — виден один подвал, прокрутки нет. */}
        <Footer />
      </ScrollView>
    </View>
  );
}

const Metric: React.FC<{ label: string; value: string; C: any; small?: boolean }> = ({ label, value, C, small }) => (
  <View style={{ gap: 2, minWidth: small ? 78 : 96 }}>
    <Typography variant="caption" style={{ color: C.textMuted }}>{label}</Typography>
    <Typography
      variant={small ? 'caption' : 'body'}
      style={{ color: C.textPrimary, fontWeight: '700' }}
    >
      {value}
    </Typography>
  </View>
);

const s = StyleSheet.create({
  root: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  // Шапка — 90px: с отступом 60 заголовок уходил под неё.
  content: { paddingTop: 120, paddingBottom: 40, paddingHorizontal: 20, maxWidth: 760, alignSelf: 'center', width: '100%', gap: 12 },
  periods: { flexDirection: 'row', gap: 8, marginTop: 4 },
  period: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 100, borderWidth: 1 },
  notice: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, padding: 12, borderRadius: 12, borderWidth: 1 },
  totals: { padding: 16, borderRadius: 16, borderWidth: 1, gap: 12 },
  totalsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 20 },
  list: { gap: 10 },
  card: { padding: 14, borderRadius: 16, borderWidth: 1 },
  cardHead: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  cover: { width: 42, height: 42, borderRadius: 10 },
  coverEmpty: { alignItems: 'center', justifyContent: 'center' },
  cardTitle: { fontWeight: '600' },
  cardRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 12, paddingTop: 10, borderTopWidth: 1 },
  opsBlock: { marginTop: 12 },
  opsTitle: { fontWeight: '700', marginBottom: 8 },
  opRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, borderBottomWidth: 1 },
  empty: { alignItems: 'center', paddingVertical: 40 },
  retry: { marginTop: 12, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 100, borderWidth: 1 },
});
